/**
 * Pure Singapore property maths. No React, no network, no side effects.
 *
 * Everything here is a plain function so it can be unit tested and reused by
 * the mortgage calculator, a future stamp duty calculator and a future
 * affordability calculator.
 */

import {
  ABSD_RATES,
  BSD_NON_RESIDENTIAL,
  BSD_RESIDENTIAL,
  type BuyerProfile,
  type DutyTier,
} from "./sgRates";

export interface MortgageInput {
  /** Purchase price in SGD. */
  propertyPrice: number;
  /** Share of the price funded by the loan, 0-100. */
  loanPercent: number;
  /** Annual nominal interest rate as a percentage, e.g. 3 for 3%. */
  interestRate: number;
  /** Loan tenure in whole years. */
  tenureYears: number;
}

export interface MortgageResult {
  loanAmount: number;
  downpayment: number;
  monthlyPayment: number;
  /** Interest portion of the very first monthly payment. */
  firstPaymentInterest: number;
  /** Principal portion of the very first monthly payment. */
  firstPaymentPrincipal: number;
  totalPaid: number;
  totalInterest: number;
  numberOfPayments: number;
}

const isPositive = (n: number) => Number.isFinite(n) && n > 0;

/**
 * Standard amortising loan payment.
 *
 *   M = P * [ r(1 + r)^n ] / [ (1 + r)^n - 1 ]
 *
 * where r is the monthly rate and n is the number of monthly payments.
 * A zero interest rate falls back to simple division, which the formula above
 * cannot express (it divides by zero).
 */
export function monthlyPayment(
  principal: number,
  annualRatePercent: number,
  tenureYears: number
): number {
  const n = Math.round(tenureYears * 12);
  if (!isPositive(principal) || !isPositive(n)) return 0;

  const r = annualRatePercent / 100 / 12;
  if (r === 0) return principal / n;

  const growth = Math.pow(1 + r, n);
  return (principal * (r * growth)) / (growth - 1);
}

export function calculateMortgage(input: MortgageInput): MortgageResult {
  const { propertyPrice, loanPercent, interestRate, tenureYears } = input;

  const price = Math.max(0, propertyPrice || 0);
  const pct = Math.min(100, Math.max(0, loanPercent || 0));
  const loanAmount = (price * pct) / 100;
  const downpayment = price - loanAmount;

  const numberOfPayments = Math.round(Math.max(0, tenureYears || 0) * 12);
  const payment = monthlyPayment(loanAmount, interestRate, tenureYears);

  const firstPaymentInterest = (loanAmount * (interestRate / 100)) / 12;
  const firstPaymentPrincipal = Math.max(0, payment - firstPaymentInterest);

  const totalPaid = payment * numberOfPayments;
  const totalInterest = Math.max(0, totalPaid - loanAmount);

  return {
    loanAmount,
    downpayment,
    monthlyPayment: payment,
    firstPaymentInterest,
    firstPaymentPrincipal,
    totalPaid,
    totalInterest,
    numberOfPayments,
  };
}

export interface AmortisationYear {
  year: number;
  principalPaid: number;
  interestPaid: number;
  balance: number;
}

/** Year-by-year breakdown of the loan. Used by the repayment chart. */
export function amortisationSchedule(
  input: MortgageInput
): AmortisationYear[] {
  const { loanAmount, monthlyPayment: payment, numberOfPayments } =
    calculateMortgage(input);
  if (!isPositive(loanAmount) || !isPositive(numberOfPayments)) return [];

  const r = input.interestRate / 100 / 12;
  const rows: AmortisationYear[] = [];

  let balance = loanAmount;
  let principalThisYear = 0;
  let interestThisYear = 0;

  for (let month = 1; month <= numberOfPayments; month++) {
    const interest = balance * r;
    const principal = Math.min(payment - interest, balance);

    balance = Math.max(0, balance - principal);
    principalThisYear += principal;
    interestThisYear += interest;

    if (month % 12 === 0 || month === numberOfPayments) {
      rows.push({
        year: Math.ceil(month / 12),
        principalPaid: principalThisYear,
        interestPaid: interestThisYear,
        balance,
      });
      principalThisYear = 0;
      interestThisYear = 0;
    }
  }

  return rows;
}

/**
 * Walk a tiered duty table. IRAS rounds duty DOWN to the nearest dollar and
 * charges a minimum of $1 on any dutiable purchase.
 */
function tieredDuty(price: number, tiers: DutyTier[]): number {
  if (!isPositive(price)) return 0;

  let remaining = price;
  let duty = 0;

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const slice = tier.upTo === null ? remaining : Math.min(remaining, tier.upTo);
    duty += slice * tier.rate;
    remaining -= slice;
  }

  return Math.max(1, Math.floor(duty));
}

/** Buyer's Stamp Duty. Rates effective on or after 15 Feb 2023. */
export function buyerStampDuty(
  price: number,
  propertyClass: "residential" | "non-residential" = "residential"
): number {
  return tieredDuty(
    price,
    propertyClass === "residential" ? BSD_RESIDENTIAL : BSD_NON_RESIDENTIAL
  );
}

/** Additional Buyer's Stamp Duty. Rates effective on or after 27 Apr 2023. */
export function additionalBuyerStampDuty(
  price: number,
  profile: BuyerProfile
): number {
  const rate = ABSD_RATES[profile] ?? 0;
  if (!isPositive(price) || rate === 0) return 0;
  return Math.max(1, Math.floor(price * rate));
}

export function formatSgd(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return "S$ 0";
  return `S$ ${value.toLocaleString("en-SG", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
}
